// hooks.ts
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useEffect, useState } from "react";
import {
  getCanvasTemplate,
  saveCanvasTemplate,
  updateCanvasTemplate,
  getTemplateOptions,
  getCanvasTemplateDetail,
} from "./api";
import { CanvasComponent, CanvasLayout, ComponentConfig } from "./types";
import { queryClient } from "@shared/api/queries/queryClient";

export interface UseCanvasTemplateReturn {
  components: CanvasComponent[];
  setComponents: (components: CanvasComponent[]) => void;
  availableComponents: any[];
  availableTemplates: any[];
  templateName: string;
  setTemplateName: (name: string) => void;
  isLoading: boolean;
  isLoadingDetail: boolean;
  queryErrorDetail: Error | null;
  error: string | null;
  saveTemplate: () => Promise<void>;
  isSaving: boolean;
  updateTemplate: () => Promise<void>;
  isUpdating: boolean;
  templateId: number | null;
  refetch: () => void;
  loadTemplate: (templateId: string) => void;
  componentConfig: ComponentConfig;
  setComponentConfig: (config: ComponentConfig) => void;
  isLoadingBaseTemplate: boolean;
}

export function useCanvasTemplate(
  templateBaseId?: string,
  id?: string
): UseCanvasTemplateReturn {
  const [components, setComponents] = useState<CanvasComponent[]>([]);
  const [availableComponents, setAvailableComponents] = useState<any[]>([]);
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [baseTemplateId, setBaseTemplateId] = useState<number | null>(null);
  const [componentConfig, setComponentConfig] = useState<ComponentConfig>({});
  const [isLoadingBaseTemplate, setIsLoadingBaseTemplate] = useState(false);

  // Fetch available templates or configured template
  const {
    data: templateData,
    isPending: isLoading,
    error: queryError,
    refetch,
  } = useSmartQuery(
    [QueryKeyFactory.module.bx.canvasTemplate.template()],
    () => {
      // New template - get available base templates
      return getTemplateOptions();
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: true,
    }
  );

  const {
    data: templateDataDetail,
    isPending: isLoadingDetail,
    error: queryErrorDetail,
  } = useSmartQuery(
    [QueryKeyFactory.module.bx.canvasTemplate.details(), templateBaseId],
    () => {
      if (templateBaseId) {
        return getCanvasTemplateDetail(templateBaseId);
      }
      return Promise.resolve(null);
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: true,
    }
  );

  // Save mutation
  const {
    mutateAsync: saveTemplateMutation,
    isPending: isSaving,
    error: saveError,
  } = useSmartMutation(saveCanvasTemplate);

  // Update mutation
  const {
    mutateAsync: updateTemplateMutation,
    isPending: isUpdating,
    error: updateError,
  } = useSmartMutation(
    ({ id, payload }: { id: string; payload: any }) =>
      updateCanvasTemplate(id, payload),
    {
      onSuccess: () => {
        console.log("debug temp successs");
        queryClient.invalidateQueries({ queryKey: ["bx", "billing", "list"] });
      },
    }
  );

  // Load specific base template when user selects from dropdown
  const loadTemplate = async (templateId: string) => {
    try {
      setIsLoadingBaseTemplate(true);
      setComponents([]); // Clear existing components
      setComponentConfig({}); // Clear existing config

      // Fetch the base template with its components
      const data = await getCanvasTemplate(templateId);
      if (data?.result) {
        processBaseTemplateData(data.result);
      }
    } catch (error) {
      console.error("Error loading template:", error);
    } finally {
      setIsLoadingBaseTemplate(false);
    }
  };

  // Process base template data (for new templates)
  const processBaseTemplateData = (result: any) => {
    // Set the base template ID for saving later
    setBaseTemplateId(result.id);

    // Set available components from the base template
    if (result.components) {
      setAvailableComponents(result.components);
    }

    // Load default layout if available
    if (result.defaultLayout && Object.keys(result.defaultLayout).length > 0) {
      const defaultComponents: CanvasComponent[] = [];
      const defaultConfig = result.defaultConfig || {};

      // Create components from default layout
      for (const [key, layout] of Object.entries(result.defaultLayout)) {
        const layoutData = layout as any;
        const componentDef = result.components.find(
          (c: any) => c.id === layoutData.componentId
        );

        if (componentDef) {
          const config = defaultConfig[key] || {};

          defaultComponents.push({
            id: key,
            componentId: layoutData.componentId,
            type: componentDef.componentName,
            x: layoutData.x || 0,
            y: layoutData.y || 0,
            width: layoutData.width || componentDef.defaultSize?.width || 300,
            height:
              layoutData.height || componentDef.defaultSize?.height || 150,
            zIndex: layoutData.zIndex || 1,
            rotation: layoutData.rotation || 0,
            isVisible: true,
            isLocked: false,
            content: componentDef,
            style: config.style || {
              backgroundColor: "#ffffff",
              borderColor: "#e5e7eb",
              borderWidth: 1,
              borderStyle: "solid",
              borderRadius: 0,
              padding: 10,
              fontSize: 14,
              color: "#374151",
              textAlign: "left",
            },
          });
        }
      }

      setComponents(defaultComponents);
      setComponentConfig(defaultConfig);
    } else {
      // No default layout - start with empty canvas
      setComponents([]);
      setComponentConfig({});
    }

    // Keep template name empty for user to fill
    if (!templateName) {
      setTemplateName("");
    }
  };

  // Process configured template data (for editing existing template)
  const processConfiguredTemplateData = (result: any) => {
    // Set template details
    setTemplateName(result?.templateName || "");
    setTemplateId(result?.id || null);
    setBaseTemplateId(result?.baseTemplate || null);

    // First, load the base template to get available components
    if (result?.baseTemplate) {
      getCanvasTemplate(result.baseTemplate.toString())
        .then((baseData) => {
          if (baseData?.result?.components) {
            setAvailableComponents(baseData.result.components);

            // After loading base components, process the canvas layout
            processCanvasLayout(result, baseData.result.components);
          }
        })
        .catch((error) => {
          console.error("Error loading base template components:", error);
        });
    }
  };

  // Process canvas layout for configured template
  const processCanvasLayout = (result: any, baseComponents: any[]) => {
    if (
      result?.configured_sequence ||
      result?.canvasLayout ||
      result?.defaultConfig
    ) {
      const sequence =
        result.configured_sequence ||
        result.canvasLayout ||
        result.defaultConfig;
      const canvasComponents: CanvasComponent[] = [];

      for (const [key, layout] of Object.entries(sequence)) {
        const layoutData = layout as any;

        // Find component definition from base components
        const componentDef = baseComponents.find(
          (c: any) => c.id === layoutData.componentId
        );

        if (componentDef) {
          const config =
            result.configuration_json?.[key] ||
            result.componentConfig?.[key] ||
            result.defaultLayout ||
            {};

          canvasComponents.push({
            id: key,
            componentId: layoutData.componentId,
            type: componentDef.componentName,
            x: layoutData.x || 0,
            y: layoutData.y || 0,
            width: layoutData.width || componentDef.defaultSize?.width || 300,
            height:
              layoutData.height || componentDef.defaultSize?.height || 150,
            zIndex: layoutData.zIndex || 1,
            rotation: layoutData.rotation || 0,
            isVisible: true,
            isLocked: false,
            content: componentDef,
            style: config.style || {
              backgroundColor: "#ffffff",
              borderColor: "#e5e7eb",
              borderWidth: 1,
              borderStyle: "solid",
              borderRadius: 0,
              padding: 10,
              fontSize: 14,
              color: "#374151",
              textAlign: "left",
            },
          });
        }
      }

      setComponents(canvasComponents);

      // Set component config
      if (result.configuration_json || result.componentConfig) {
        setComponentConfig(result.configuration_json || result.componentConfig);
      }
    }
  };

  // Initial data loading
  useEffect(() => {
    if (templateData?.result) {
      if (templateBaseId) {
        // Editing existing configured template
        processConfiguredTemplateData(templateDataDetail);
      }

      // New template - set available templates
      if (templateData.result.availableTemplates) {
        setAvailableTemplates(templateData.result.availableTemplates);
      } else if (Array.isArray(templateData.result)) {
        // If API returns array of templates
        setAvailableTemplates(
          templateData.result.map((t: any) => ({
            id: t.id,
            name: t.templateName || t.template_name,
            alias: t.templateAlias || t.template_alias,
          }))
        );
      }
    }
  }, [templateData, templateBaseId, templateDataDetail]);

  // Transform components to save payload
  const transformToSavePayload = () => {
    const canvasLayout: CanvasLayout = {};
    const finalComponentConfig: ComponentConfig = { ...componentConfig };

    components.forEach((component) => {
      canvasLayout[component.id] = {
        x: component.x,
        y: component.y,
        width: component.width,
        height: component.height,
        zIndex: component.zIndex,
        rotation: component.rotation,
        componentId: component.componentId,
      };

      // Ensure each component has a config entry
      if (!finalComponentConfig[component.id]) {
        finalComponentConfig[component.id] = {
          style: component.style,
          fields: {},
        };
      } else {
        // Update style to match current component style
        finalComponentConfig[component.id].style = component.style;
      }
    });

    return {
      remoteUtilityId: getRemoteUtilityId(),
      configuredTemplateName: templateName,
      baseTemplate: baseTemplateId!,
      isActive: true,
      canvasLayout,
      componentConfig: finalComponentConfig,
    };
  };

  const saveTemplate = async () => {
    if (!baseTemplateId) {
      throw new Error("Please select a base template first");
    }

    if (!templateName.trim()) {
      throw new Error("Template name is required");
    }

    if (components.length === 0) {
      throw new Error("Please add at least one component to the canvas");
    }

    const payload = transformToSavePayload();
    const result = await saveTemplateMutation(payload);

    // Update templateId after successful save
    if (result?.id) {
      setTemplateId(result.id);
    }

    refetch();
  };

  const updateTemplate = async () => {
    if (!templateId) {
      throw new Error("No template ID available for update");
    }

    if (!templateName.trim()) {
      throw new Error("Template name is required");
    }

    const payload = transformToSavePayload();
    await updateTemplateMutation({ id: templateId.toString(), payload });
    refetch();
  };

  const error =
    queryError?.message || saveError?.message || updateError?.message || null;

  return {
    components,
    setComponents,
    availableComponents,
    availableTemplates,
    templateName,
    setTemplateName,
    isLoading,
    isLoadingDetail,
    error,
    queryErrorDetail,
    saveTemplate,
    isSaving,
    updateTemplate,
    isUpdating,
    templateId,
    refetch,
    loadTemplate,
    componentConfig,
    setComponentConfig,
    isLoadingBaseTemplate,
  };
}
